"use client";

import { trackUmamiEvent } from "@analytics";

export type GenerationTool =
	| "image-generator"
	| "nano-banana"
	| "ai-video"
	| "image-to-video"
	| "music";

const MAX_MESSAGE = 150;

// Checked before PAYWALL: an upstream moderation message can mention credits
// (e.g. "credits refunded") and must still count as a failure.
const REASONS: Array<[string, RegExp]> = [
	[
		"moderation",
		/moderation|nsfw|sensitive|flagged|inappropriate|safety|content polic|unsafe|violat|guidelines/i,
	],
	["no-output", /NO_MEDIA_GENERATED/],
	["timeout", /timeout|timed out/i],
	["lost-connection", /lost connection|not found|failed to get task status/i],
];

// Paywall hits (credits, trial, paid-only model/resolution) aren't failures —
// they already show up as paywall-viewed — so they are never reported here.
const PAYWALL = /insufficient|credit|trial|paid|premium|you need/i;

/**
 * Report a failed generation to Umami. Image generation doesn't persist failed
 * attempts in the database, so without this we can't see whether failures are
 * rising. One event for every tool and model; slice by the properties.
 */
export function reportGenerationFailure(
	tool: GenerationTool,
	model: string | undefined,
	error: unknown,
) {
	try {
		const raw =
			error instanceof Error ? error.message : String(error ?? "unknown");
		// Some hooks put the machine-readable code on the error, not the message
		const code = (error as { code?: unknown } | null)?.code;
		const text = typeof code === "string" ? `${code} ${raw}` : raw;
		const reason = REASONS.find(([, pattern]) => pattern.test(text))?.[0];
		// SERVER_ERROR is an untyped 5xx; real paywall hits come back typed
		if (!reason && code !== "SERVER_ERROR" && PAYWALL.test(text)) {
			return;
		}

		void trackUmamiEvent("generation-failed", {
			tool,
			model: model || "unknown",
			reason: reason ?? "other",
			message:
				raw.length > MAX_MESSAGE
					? `${raw.slice(0, MAX_MESSAGE)}…`
					: raw,
		});
	} catch {
		// Reporting is best-effort and must never break the page.
	}
}
